import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { client, refreshToken, VidAppJWT } from "@/api";
import { jwtDecode } from "jwt-decode";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { useLogout } from "@/app/modules/User/hooks/useLogout.tsx";

interface ContextValue {
  token: string;
  setToken: (t: string) => void;
  isRefreshing: boolean;
  refresh: ({ onSettled }: { onSettled?: () => void }) => void;
}

interface ProviderProps {
  children: ReactNode | ReactNode[];
}

type OptionalContextValue = ContextValue | undefined;

type RequestQueueItem = [
  //eslint-disable-next-line
  (value: AxiosResponse<any>) => void,
  AxiosRequestConfig,
];
const requestQueue: RequestQueueItem[] = [];

const AuthContext = createContext<OptionalContextValue>(undefined);
const AuthProvider = ({ children }: ProviderProps) => {
  const [token, setToken] = useState<string>(localStorage.getItem("jwt") ?? "");
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const logout = useLogout();

  const { uid, exp } = useMemo(() => {
    if (token) {
      return jwtDecode<VidAppJWT>(token);
    }
    return {} as VidAppJWT;
  }, [token]);

  const refresh = useCallback(
    async ({ onSettled }: { onSettled?: () => void }) => {
      if (!isRefreshing && uid) {
        setIsRefreshing(true);
        console.info("Refreshing access token");
        try {
          await refreshToken({ user_id: uid })
            .then((token) => {
              if (token) {
                setToken(token);
              }
            })
            .catch()
            .finally(() => {
              // Runs regardless of whether the refresh succeeds
              if (onSettled) {
                onSettled();
              }
            });
          while (requestQueue.length > 0) {
            const [resolve, request] = requestQueue.shift() as RequestQueueItem;
            console.log(`Retrying queued request to ${request.url}`);
            try {
              resolve(await client(request));
            } catch (e) {
              console.error(
                `Failed retry on request to ${request.url} with error: ${e}`,
              );
            }
          }
        } catch (err) {
          console.error("Error during refresh", err);
          logout.mutate({ user_id: uid });
        }
        setIsRefreshing(false);
      }
    },
    [setIsRefreshing, isRefreshing, uid, logout],
  );

  const isExpired = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return now > exp;
  }, [exp]);
  const isExpiring = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return now > exp - 60 * 30; // Expiring in 30 minutes
  }, [exp]);

  useEffect(() => {
    // On page load we refresh the token if its expired
    // ProtectedRoutes will return null if token is expired
    if (isExpired || isExpiring) {
      refresh({});
    }
  }, [isExpired, isExpiring, refresh]);

  useEffect(() => {
    client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        // Handle expired access token, use refresh cookie to re-retrieve access token and retry the original request
        const originalRequest = error.config;

        // Ignore error handling from defined routes
        const ignoreRouteList = [
          "https://passport.vidapp.com/auth/token/refresh",
          "https://passport.vidapp.com/auth/token",
          "https://passport.vidapp.com/auth/token/refresh",
        ];
        if (ignoreRouteList.includes(originalRequest.url))
          return Promise.reject(error);

        // Retry requests which need Auth
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Check if access token is expired, to avoid multiple refreshes
          if (isExpired) {
            // Queue the request to be resolved when the token is refreshed
            const requestPromise = new Promise((resolve) =>
              requestQueue.push([resolve, originalRequest]),
            );
            refresh({}); // Dont await
            return requestPromise;
          } else {
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }, [isExpired, refresh]);

  return (
    <AuthContext.Provider value={{ token, setToken, refresh, isRefreshing }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
